/**
 * Pure roadmap domain. No React, no DOM, no framework imports — this module is the
 * single source of truth for what the roadmap says, and it is testable on its own.
 * The UI adapter lives in `src/components/roadmap/`.
 */

/**
 * `in-progress` is the quarter we are actually in. It exists so the roadmap can say
 * "we are working on this now" without either lying (`shipped`) or reading as distant
 * future (`planned`).
 */
export type MilestoneStatus = 'shipped' | 'in-progress' | 'planned';

export interface MilestoneLink {
  href: string;
  label: string;
}

export interface RoadmapMilestone {
  kind: 'milestone';
  id: string;
  /**
   * Words only. Newline characters are rendered as line breaks by the adapter, and the
   * paw that ends every roadmap title is drawn by the adapter too — it used to be
   * U+1F43E typed in here, which pixel-sampled 1.08:1 on the card (invisible) and was
   * a different full-colour bitmap on every OS.
   */
  title: string;
  description: string;
  status: MilestoneStatus;
  /**
   * Overrides the generic pill copy for this milestone's status. The pill is still
   * *decided* by `status` — see `getStatusPill` — this only sharpens its wording.
   */
  pillLabel?: string;
  link?: MilestoneLink;
  /** Mirrors the legacy `roadmap__item--extra-margin` modifier on this slot. */
  extraMargin?: boolean;
}

export interface RoadmapMilestoneGroup {
  kind: 'group';
  id: string;
  /** Display title of the folded card, in the same voice as a milestone title. */
  title: string;
  /** One line naming what is behind the fold. A count names nothing. */
  payload: string;
  /** Label of the control that folds the group back up. */
  collapseLabel: string;
  milestones: RoadmapMilestone[];
}

export type RoadmapEntry = RoadmapMilestone | RoadmapMilestoneGroup;

export const RECAP_GROUP_ID = '2025-recap';

const MILESTONE_2012: RoadmapMilestone = {
  kind: 'milestone',
  id: '2012-10-10',
  title: '10 October 2012:\nZeus is born.',
  description:
    'The real dog behind the character is born in Macedonia.',
  status: 'shipped',
};

const MILESTONE_2022: RoadmapMilestone = {
  kind: 'milestone',
  id: '2022-02-07',
  title: '7 February 2022: Zeus is drawn!',
  description:
    'ND Haus draws Zeus for the first time.',
  status: 'shipped',
};

const MILESTONES_2025: RoadmapMilestone[] = [
  {
    kind: 'milestone',
    id: '2025-05-21',
    title: '21 May 2025:\n$ZEUS token launches.',
    description:
      '$ZEUS launches on Ethereum with the contract #CC8, with no VCs and no insiders. Memes start flowing on Twitter and Telegram and the token gets listed on DEX tools and trackers.',
    status: 'shipped',
  },
  {
    kind: 'milestone',
    id: '2025-06-04',
    title:
      '4 June 2025: We buy the original ZEUS NFT',
    description:
      'The #CC8 team buys the original ND Haus artwork of Zeus, so the art and the IP stay with the project.',
    status: 'shipped',
    extraMargin: true,
  },
  {
    kind: 'milestone',
    id: '2025-10-28',
    title: '28 October 2025: The Community Takes Control',
    description:
      'The community sets up a DAO on Ethereum (Tally) and votes on the ZEUS constitution. The site moves from zeuscoin.vip to zeus.army and then to pepesdog.box, and CoinMarketCap updates the project page to point to the community. New leaders step up, contributors get organised and the community commits tens of thousands of dollars a month to fund the work.',
    status: 'shipped',
    extraMargin: true,
  },
  {
    kind: 'milestone',
    id: '2025-11',
    title: 'November 2025: First Utilities',
    description:
      "Pepesdog gets a real development team. Pepesdog Tips ships as a self-custody tool to tip ZEUS on Twitter (it has since been retired), with ZEUS deployed on BASE and Wormhole as the bridge. We also launch custom ENS names, and the most active members join a private Telegram group where the strategy gets planned.",
    status: 'shipped',
  },
  {
    kind: 'milestone',
    id: '2025-12',
    title: 'December 2025: Treasury Team',
    description:
      'People who worked on Superfluid, the Ethereum Foundation and MakerDAO join as treasury advisors. Their job is to defend the price and find ways for holders to take profit without selling. We also set up OTC deals so large holders can buy or sell big amounts of ZEUS without slippage and without going through Uniswap.',
    status: 'shipped',
  },
];

const RECAP_2025: RoadmapMilestoneGroup = {
  kind: 'group',
  id: RECAP_GROUP_ID,
  title: '2025: The Takeover',
  payload:
    'Token launch, the original NFT, the DAO, first utilities and the treasury team',
  collapseLabel: 'Fold 2025 back up',
  milestones: MILESTONES_2025,
};

const MILESTONE_2026_JANUARY: RoadmapMilestone = {
  kind: 'milestone',
  id: '2026-01',
  title: 'January 2026: Agentic Meme',
  description:
    "Pepesdog gives the community AI tools to build apps, games, smart contracts and other utilities. Members who never coded before start making games, metaverses and points systems. We want every member to be able to build, so we invest in AI tools like Anthropic Opus 4.6.",
  status: 'shipped',
};

/**
 * January owns the enabling fact (the AI tools were made available). This card owns
 * what the community did with them, so the two stop making the same claim twice.
 */
const MILESTONE_2026_Q1: RoadmapMilestone = {
  kind: 'milestone',
  id: '2026-q1',
  title: 'Q1 2026: The Community Starts Building',
  description:
    'In Q1 contributors stopped waiting for the core team and shipped their own products under the Zeus brand. They built on our name, kept what they made and brought new holders with them. By March the community was working as its own development studio.',
  status: 'shipped',
};

const MILESTONE_2026_Q2: RoadmapMilestone = {
  kind: 'milestone',
  id: '2026-q2',
  title: 'Q2 2026: DeFi Tools to Defend the Price',
  description:
    "earn.pepes.dog is live. It is a self-custody product where holders put their ZEUS to work instead of selling it. Other memecoins chase CEX listings, but a listing mostly gives early holders exit liquidity to sell to new buyers. We don't sell. We hedge with shorts and earn the funding fee from perps plus the fees ZEUS makes on Uniswap, and that puts a floor under the price.",
  status: 'shipped',
  pillLabel: 'First tool live',
  link: {
    href: 'https://earn.pepes.dog',
    label: 'Open earn.pepes.dog',
  },
};

const MILESTONE_2026_Q3: RoadmapMilestone = {
  kind: 'milestone',
  id: '2026-q3',
  title: 'Q3 2026: Our Own Stablecoin',
  description:
    'Our own stablecoin, backed by the treasury the community has built since it took control. Holders can get value out of their position without selling a single ZEUS, by borrowing against what they hold. It is backed by income we already earn: perps funding fees, Uniswap fees and the DeFi tools from Q2. With that behind it, the price has real support.',
  status: 'shipped',
  pillLabel: 'Built',
  link: {
    href: 'https://dollarize.fun/stablecoins/0x0f7dc5d02cc1e1f5ee47854d534d332a1081ccc8#mint',
    label: 'See USDZEUS on dollarize',
  },
};

const ROADMAP_ENTRIES: RoadmapEntry[] = [
  MILESTONE_2012,
  MILESTONE_2022,
  RECAP_2025,
  MILESTONE_2026_JANUARY,
  MILESTONE_2026_Q1,
  MILESTONE_2026_Q2,
  MILESTONE_2026_Q3,
];

/** The roadmap, in chronological order, with the 2025 stretch folded into one group. */
export function getRoadmapEntries(): RoadmapEntry[] {
  return ROADMAP_ENTRIES;
}

export function isRecapGroup(entry: RoadmapEntry): entry is RoadmapMilestoneGroup {
  return entry.kind === 'group';
}

/**
 * How many `.roadmap__item` slots the section renders. A group still occupies one slot
 * per milestone, collapsed or not, because `index.min.css` positions each card along the
 * road illustration with hand-tuned `:nth-child()` rules.
 */
export function countRoadmapSlots(entries: RoadmapEntry[] = ROADMAP_ENTRIES): number {
  return entries.reduce(
    (total, entry) => total + (isRecapGroup(entry) ? entry.milestones.length : 1),
    0
  );
}

/** Every milestone in roadmap order, with grouped ones inlined where they sit. */
export function flattenMilestones(
  entries: RoadmapEntry[] = ROADMAP_ENTRIES
): RoadmapMilestone[] {
  return entries.flatMap((entry) =>
    isRecapGroup(entry) ? entry.milestones : [entry]
  );
}

/** Generic pill copy per status. `planned` earns no pill: it is the default state. */
const PILL_BY_STATUS: Partial<Record<MilestoneStatus, string>> = {
  shipped: 'Shipped',
  'in-progress': 'In progress',
};

/**
 * The pill a milestone has earned, or `null`.
 *
 * Only the frontier of the roadmap gets one: whatever is in progress right now, and
 * the most recent thing that shipped. Eight `SHIPPED` pills would be noise — a card's
 * position on the road already carries "done" — and a `SHIPPED` pill on a programme
 * that is still running contradicts the card's own copy.
 */
export function getStatusPill(
  milestone: RoadmapMilestone,
  entries: RoadmapEntry[] = ROADMAP_ENTRIES
): string | null {
  if (milestone.status === 'in-progress') {
    return milestone.pillLabel ?? PILL_BY_STATUS['in-progress'] ?? null;
  }
  if (milestone.status !== 'shipped') {
    return null;
  }

  const shipped = flattenMilestones(entries).filter(
    (candidate) => candidate.status === 'shipped'
  );
  const latest = shipped[shipped.length - 1];

  return latest && latest.id === milestone.id
    ? milestone.pillLabel ?? PILL_BY_STATUS.shipped ?? null
    : null;
}

/** The unfold affordance's copy, counted from the group rather than hardcoded. */
export function getFoldExpandLabel(group: RoadmapMilestoneGroup): string {
  return `Unfold ${group.milestones.length} milestones`;
}

/**
 * The end-of-group control's accessible name. It does the same thing as the control on
 * the header plus one more — it puts you back at the top — and sharing a name with it
 * meant a screen reader's button list showed two indistinguishable "Fold 2025 back up"
 * entries. Built from `collapseLabel` rather than beside it so the visible label stays
 * the start of the accessible one (WCAG 2.5.3 Label in Name).
 */
export function getFoldCollapseEndLabel(group: RoadmapMilestoneGroup): string {
  return `${group.collapseLabel} and return to the top`;
}

/** Splits a milestone title into the lines the adapter separates with `<br />`. */
export function getTitleLines(title: string): string[] {
  return title.split('\n');
}
